import { useEffect, useRef, useCallback, useState } from 'react';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/config/api';

/**
 * Custom hook for real-time hub updates via WebSocket
 * 
 * @param {string} hubId - The hub ID to subscribe to
 * @param {string} token - Auth token for WebSocket connection
 * @param {object} callbacks - Event callbacks for different update types
 * @returns {object} - WebSocket state and methods
 */
export const useHubWebSocket = (hubId, token, callbacks = {}) => {
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState(null);

  const {
    onHubUpdated,
    onMemberJoined,
    onMemberLeft,
    onMemberRoleChanged,
    onHubDeleted,
    onChannelCreated,
    onChannelDeleted,
    onBoardCreated,
    onBoardDeleted,
    showToasts = true
  } = callbacks;

  const connect = useCallback(() => {
    if (!hubId || !token) return;
    
    // Close existing connection
    if (wsRef.current) {
      wsRef.current.close();
    }

    const wsUrl = `${BACKEND_URL.replace('http', 'ws')}/api/ws/hub/${hubId}?token=${token}`;
    
    try {
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        console.log(`Hub WebSocket connected for hub ${hubId}`);
        setIsConnected(true);
      };

      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          setLastEvent(data);

          switch (data.type) {
            case 'connected':
              console.log('Hub WebSocket connection confirmed');
              break;

            case 'hub_updated':
              if (showToasts) {
                toast.info(`Hub settings updated`);
              }
              onHubUpdated?.(data.hub);
              break;

            case 'member_joined':
              if (showToasts) {
                toast.success(`${data.member?.name || 'Someone'} joined ${data.hub_name || 'the hub'}`);
              }
              onMemberJoined?.(data.member);
              break;

            case 'member_left':
              if (showToasts) {
                toast.info(`${data.user_name || 'A member'} left ${data.hub_name || 'the hub'}`);
              }
              onMemberLeft?.(data.user_id);
              break;

            case 'member_role_changed':
              if (showToasts) {
                toast.info(`Member role changed to ${data.new_role}`);
              }
              onMemberRoleChanged?.(data.user_id, data.new_role);
              break;

            case 'hub_deleted':
              if (showToasts) {
                toast.warning(`${data.hub_name || 'Hub'} has been deleted`);
              }
              onHubDeleted?.(data.hub_id);
              break;

            case 'channel_created':
              if (showToasts) {
                toast.success(`New channel: ${data.channel?.name || 'Untitled'}`);
              }
              onChannelCreated?.(data.channel);
              break;

            case 'channel_deleted':
              if (showToasts) {
                toast.info(`Channel "${data.channel_name}" was deleted`);
              }
              onChannelDeleted?.(data.channel_id);
              break;

            case 'board_created':
              if (showToasts) {
                toast.success(`New board: ${data.board?.name || 'Untitled'}`);
              }
              onBoardCreated?.(data.board);
              break;

            case 'board_deleted':
              if (showToasts) {
                toast.info(`Board "${data.board_name}" was deleted`);
              }
              onBoardDeleted?.(data.board_id);
              break;

            case 'pong':
              // Heartbeat response
              break;

            default:
              console.log('Unknown hub WebSocket message:', data);
          }
        } catch (err) {
          console.error('Failed to parse hub WebSocket message:', err);
        }
      };

      wsRef.current.onerror = (error) => {
        console.error('Hub WebSocket error:', error);
        setIsConnected(false);
      };

      wsRef.current.onclose = () => {
        console.log('Hub WebSocket closed');
        setIsConnected(false);
        
        // Attempt to reconnect after 5 seconds
        reconnectTimeoutRef.current = setTimeout(() => {
          if (hubId && token) {
            console.log('Attempting to reconnect hub WebSocket...');
            connect();
          }
        }, 5000);
      };
    } catch (err) {
      console.error('Failed to create hub WebSocket:', err);
    }
  }, [hubId, token, showToasts, onHubUpdated, onMemberJoined, onMemberLeft, 
      onMemberRoleChanged, onHubDeleted, onChannelCreated, onChannelDeleted,
      onBoardCreated, onBoardDeleted]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  const sendPing = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'ping' }));
    }
  }, []);

  // Connect when hubId and token are available
  useEffect(() => {
    if (hubId && token) {
      connect();
    }
    
    return () => {
      disconnect();
    };
  }, [hubId, token, connect, disconnect]);

  // Set up heartbeat ping every 30 seconds
  useEffect(() => {
    if (!isConnected) return;
    
    const pingInterval = setInterval(sendPing, 30000);
    return () => clearInterval(pingInterval);
  }, [isConnected, sendPing]);

  return {
    isConnected,
    lastEvent,
    connect,
    disconnect,
    sendPing
  };
};

export default useHubWebSocket;
